import { useEffect, useLayoutEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { exchangeCodeForToken } from '@/services/keycloakService'

// ─── Helpers ───────────────────────────────────────────────────────

function isSafeInternalPath(path: string): boolean {
  return typeof path === 'string' && /^\/($|[^/])/.test(path)
}

function getPostLoginDestination(): string {
  const stored = localStorage.getItem('post-login-redirect')
  localStorage.removeItem('post-login-redirect')
  return stored && isSafeInternalPath(stored) ? stored : '/home'
}

async function getPublicLoginUrl(): Promise<string> {
  const base = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:5001'
  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'parkeze-reserve'
  const redirect = encodeURIComponent(window.location.origin + '/')

  // Generate PKCE code verifier + challenge
  const verifier = generateCodeVerifier()
  const challenge = await generateCodeChallenge(verifier)
  sessionStorage.setItem('pkce-verifier', verifier)

  // No `organization` here. It is already a default scope on parkeze-reserve,
  // so it is applied to every token regardless -- and asking for it *as well*
  // makes Keycloak 26.7 fail with 500 "Duplicate key organization" before the
  // login page renders. The operator dashboard sends no scope at all and has
  // never hit it.
  return `${base}/realms/parkeze/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${redirect}&response_type=code&scope=openid&code_challenge=${challenge}&code_challenge_method=S256`
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(64)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

// ─── URL-based phase detection (runs once, synchronously) ──────────

type Phase = 'has-token' | 'has-code' | 'no-auth'

function detectPhase(): Phase {
  const params = new URLSearchParams(window.location.search)

  if (params.get('code')) return 'has-code'

  const tokenStr = localStorage.getItem('keycloak-token')
  if (tokenStr) {
    try {
      const td = JSON.parse(tokenStr)
      if (td?.access_token) {
        const exp = td.expires_at ? new Date(td.expires_at) : null
        if (!exp || exp > new Date()) return 'has-token'
      }
    } catch { /* fall through */ }
    localStorage.removeItem('keycloak-token')
  }

  return 'no-auth'
}

// ─── Component ─────────────────────────────────────────────────────

export default function LandingPage() {
  const [phase] = useState<Phase>(detectPhase)
  const [error, setError] = useState(false)
  const navigate = useNavigate()

  // Synchronous branches (before browser paint)
  useLayoutEffect(() => {
    if (phase === 'has-token') {
      navigate(getPostLoginDestination(), { replace: true })
    } else if (phase === 'no-auth') {
      // Guard against redirect loops: if we have redirected recently, stop
      const lastRedirect = Number(sessionStorage.getItem('kc-redirect-at') || '0')
      const now = Date.now()
      if (now - lastRedirect < 5000) {
        // Already redirected within the last 5 s — break the loop
        setError(true)
        return
      }
      sessionStorage.setItem('kc-redirect-at', String(now))
      getPublicLoginUrl().then((url) => { window.location.href = url })
    }
  }, [phase, navigate])

  // OAuth code exchange
  useEffect(() => {
    if (phase !== 'has-code') return

    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')!

    // Store realm info for token refresh and logout
    const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:5001'
    localStorage.setItem(
      'realm-info',
      JSON.stringify({
        realm: 'parkeze',
        name: 'parkeze',
        keycloakUrl,
        loginUrl: `${keycloakUrl}/realms/parkeze/protocol/openid-connect/auth`,
      }),
    )

    exchangeCodeForToken(code, 'parkeze', sessionStorage.getItem('pkce-verifier') ?? undefined)
      .then(() => {
        sessionStorage.removeItem('kc-redirect-at')
        sessionStorage.removeItem('pkce-verifier')
        window.history.replaceState({}, '', '/')
        navigate(getPostLoginDestination(), { replace: true })
      })
      .catch((err) => {
        console.error('Login failed:', err instanceof Error ? err.message : 'Unknown error')
        setError(true)
      })
  }, [phase, navigate])

  // ─── Render ──────────────────────────────────────────────────────

  if (phase === 'has-code' && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Signing in...</p>
        </div>
      </div>
    )
  }

  if (error) {
    const handleRetry = () => {
      localStorage.removeItem('keycloak-token')
      localStorage.removeItem('realm-info')
      sessionStorage.removeItem('kc-redirect-at')
      sessionStorage.removeItem('pkce-verifier')
      window.history.replaceState({}, '', '/')
      getPublicLoginUrl().then((url) => { window.location.href = url })
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-muted-foreground">Sign-in failed.</p>
          <button
            onClick={handleRetry}
            className="px-6 py-2.5 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  return null
}
